import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { FloatButton, Input, Tag } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { popupcontext } from "../../../context/Popupscontext";
import { filtrationcontext } from "../../../context/Filtercontext";
import { useGetAllSubmittalsQuery } from "../../../Redux/service/Submittals/Submittals";
import Loader from "../../Helpers/Loader/Loader";
import Display from "../../Helpers/Table/Table Actions/Display";
import trans from "../../../utils";
import "../../../Styles/Mobile/irMobile.css";

const SubmittalsMobile = () => {
  const { showDetails, setReloadData, reloadData, currentSubmittalId } =
    useContext(popupcontext);
  const { searchQuery, setSearchQuery } = useContext(filtrationcontext);

  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState([]);
  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef(null);

  const PAGE_SIZE = 20;
  const statusColors = {
    PENDING_QA: `orange`,
    PENDING_CONSULTANT: `orange`,
    APPROVED: `green`,
    REJECTED: `red`,
    REVISION_REQUIRED: `purple`,
    RETURNED_TO_CONTRACTOR: `default`,
  };

  const { data, isLoading, isFetching } = useGetAllSubmittalsQuery(
    {
      page,
      limit: PAGE_SIZE,
      search: searchQuery,
      // Mobile list keeps it simple: no sort/filter for now
    },
    { skip: !page },
  );

  const total = data?.pagination?.total ?? 0;
  const hasMore = accumulated.length < total;

  // Accumulate pages for infinite scroll
  useEffect(() => {
    if (!data?.data) return;
    if (page === 1) {
      setAccumulated(data.data);
    } else {
      setAccumulated((prev) => [...prev, ...(data.data || [])]);
    }
    loadingMoreRef.current = false;
  }, [data?.data, page]);

  // Reset when search changes
  useEffect(() => {
    setPage(1);
    setAccumulated([]);
  }, [searchQuery]);

  // Refresh when global reloadData is triggered (e.g. after create/edit)
  useEffect(() => {
    if (reloadData) {
      setPage(1);
      setAccumulated([]);
      setReloadData(false);
    }
  }, [reloadData, setReloadData]);

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || !hasMore || isFetching) return;
    loadingMoreRef.current = true;
    setPage((p) => p + 1);
  }, [hasMore, isFetching]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || isFetching) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: null, rootMargin: "100px", threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, hasMore, isFetching]);

  const handleCardClick = (record) => {
    showDetails("submittal", record.id);
  };

  const { handleShow } = useContext(popupcontext);

  if (isLoading && page === 1) {
    return (
      <div className="ir-mobile-loader">
        <Loader />
      </div>
    );
  }

  return (
    <div className="ir-mobile">
      <div className="ir-mobile-scroll">
        <Input
          prefix={<SearchOutlined style={{ color: "#aaa" }} />}
          placeholder={trans("Search")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="ir-mobile-search"
        />

        <div className="ir-mobile-cards">
          {accumulated.length === 0 && !isFetching ? (
            <div className="ir-mobile-empty">{trans("No Submittals")}</div>
          ) : (
            accumulated.map((record) => (
              <div
                key={record.id}
                className="ir-mobile-card"
                onClick={() => handleCardClick(record)}
              >
                <div className="ir-mobile-card-header">
                  <div className="ir-mobile-card-header-left">
                    <span className="ir-mobile-card-number">
                      {record.submittalNumber}
                    </span>{" "}
                    {record.currentRevision && (
                      <Tag
                        color={
                          record.currentRevision.startsWith("ESR")
                            ? "purple"
                            : "blue"
                        }
                      >
                        {record.currentRevision}
                      </Tag>
                    )}
                    {record.type && (
                      <span className="ir-mobile-card-subtitle">
                        {trans(record.type)}
                      </span>
                    )}
                  </div>
                  <Tag
                    color={
                      statusColors[record?.status]
                        ? statusColors[record.status]
                        : "default"
                    }
                  >
                    {record?.status ? trans(record.status) : "—"}
                  </Tag>
                </div>

                <div className="ir-mobile-card-body">
                  <div className="ir-mobile-card-row">
                    <span className="ir-mobile-card-label">
                      {trans("Project")}
                    </span>
                    <span>{record?.project?.name ?? "—"}</span>
                  </div>
                  <div className="ir-mobile-card-row">
                    <span className="ir-mobile-card-label">
                      {trans("Discipline")}
                    </span>
                    <span>
                      {record?.discipline ? trans(record.discipline) : "—"}
                    </span>
                  </div>
                  <div className="ir-mobile-card-row">
                    <span className="ir-mobile-card-label">
                      {trans("Review Date")}
                    </span>
                    <span>{record?.targetReviewDate ?? "—"}</span>
                  </div>
                  <div className="ir-mobile-card-row">
                    <span className="ir-mobile-card-label">
                      {trans("Submitted By")}
                    </span>
                    <span>{record?.submittedBy?.name ?? "—"}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div ref={sentinelRef} className="ir-mobile-sentinel" />
        {isFetching && page > 1 && (
          <div className="ir-mobile-loading-more">
            <Loader />
          </div>
        )}
      </div>

      <FloatButton
        type="primary"
        icon={<PlusOutlined />}
        className="ir-mobile-fab"
        onClick={() => handleShow("addForm")}
      />

      {currentSubmittalId && <Display submittalId={currentSubmittalId} />}
    </div>
  );
};

export default SubmittalsMobile;
